import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SelectContactsToggle,
  SellerContactsPicker,
} from '~/components/common/SellerContactsPicker';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { sellerLabel } from '~/utils';
import type { ContactDetails, RecipientOrganization } from '~/utils/contact';

export type SellerContactOrganization = RecipientOrganization & {
  hasPendingNegotiation?: boolean;
};

type SellerContactsSummaryRowProps = Readonly<{
  organization: SellerContactOrganization;
  contacts: ContactDetails[];
  loaded: boolean;
  selectedContactIds: string[];
  primaryContactId?: string;
  primaryContactMode: 'optional' | 'off';
  onChange: (
    organizationId: string,
    contactIds: string[],
    primaryContactId: string | undefined
  ) => void;
  labels: Readonly<{ noContactsSelected: string; noContacts: string }>;
  testId: string;
  disabled?: boolean;
}>;

export function SellerContactsSummaryRow({
  organization,
  contacts,
  loaded,
  selectedContactIds,
  primaryContactId,
  primaryContactMode,
  onChange,
  labels,
  testId,
  disabled = false,
}: SellerContactsSummaryRowProps) {
  const { t } = useTranslation('general');
  const [expanded, setExpanded] = useState(false);
  const pickerId = useId();
  const label = sellerLabel(organization);
  const hasContacts = contacts.length > 0;
  const selectedCount = selectedContactIds.length;

  if (organization.hasPendingNegotiation) {
    return (
      <li
        className="border-b border-neut-border last:border-b-0 px-1 py-3 flex flex-wrap items-center gap-2"
        data-testid={testId}
      >
        <span className="text-sm">{label}</span>
        <span className={`${statusBadgeClasses('pending')} whitespace-nowrap`}>
          {t('seller_contacts_summary.negotiation_outstanding')}
        </span>
      </li>
    );
  }

  return (
    <li
      className="border-b border-neut-border last:border-b-0"
      data-testid={testId}
    >
      <div className="px-1 py-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm min-w-0">
          {label}
          {selectedCount > 0 && (
            <span className="text-xs text-neut-sec ml-2">
              {t('seller_contacts_summary.selected_count', {
                count: selectedCount,
              })}
            </span>
          )}
          {loaded && hasContacts && selectedCount === 0 && (
            <span
              className={`${statusBadgeClasses('warning')} whitespace-nowrap ml-2`}
            >
              {labels.noContactsSelected}
            </span>
          )}
          {loaded && !hasContacts && (
            <span className="block text-xs text-neut-sec">
              {labels.noContacts}
            </span>
          )}
        </span>
        {loaded && (
          <SelectContactsToggle
            expanded={expanded}
            onToggle={() => setExpanded(!expanded)}
            controlsId={pickerId}
            sellerName={label}
            disabled={disabled}
            testId={`${testId}-select-contacts`}
          />
        )}
      </div>
      {loaded && expanded && (
        <SellerContactsPicker
          id={pickerId}
          organizationId={organization.id}
          sellerName={label}
          contacts={contacts}
          selectedContactIds={selectedContactIds}
          primaryContactId={primaryContactId}
          primaryContactMode={primaryContactMode}
          onChange={(selectedContacts, primaryId) =>
            onChange(
              organization.id,
              selectedContacts.map(({ id }) => id),
              primaryId
            )
          }
          disabled={disabled}
          testId={`${testId}-contacts`}
        />
      )}
    </li>
  );
}
